<template>
  <div class="user-profile__content">
    <div class="user-profile__image" :class="{'user-profile__image--is-primary': isPrimary}">
      <ion-img
        v-if="avatarSrc && imgExists"
        class="user-profile__avatar"
        :src="`${avatarSrc}?${new Date().getTime()}`"
        @ion-img-did-load="imgExists = true"
        @ion-error="imgExists = false"
      />
      <vue-avatar
        v-else
        style="width: 100%; height: 100%;"
        :username="capitalizeEachWord(userName)"
      />
      <div
        class="user-profile__icon-location"
        v-if="isTracking"
      >
        <ion-icon
          :color="isPrimary ? 'success' : 'primary'"
          src="assets/icon/location.svg"
        />
      </div>
    </div>
    <div class="user-profile__info">
      <ion-button
        class="user-profile__btn-notice ion-no-padding"
        fill="clear"
        v-if="!hideNoticeButton && !isDesktop"
        @click="navigate({name: RouteNamesEnum.Notifications})"
      >
        <span v-if="hasUnreadEvents" class="user-profile__btn-notice__badge" />
        <ion-icon
          slot="icon-only"
          src="assets/icon/notice.svg"/>
      </ion-button>
      <ion-text class="user-profile__team-name">{{ teamName }}</ion-text>
      <ion-text class="user-profile__name">{{ userName }}</ion-text>
      <ion-text class="user-profile__id">{{ titleId }}: {{ userId }}</ion-text>
      <ion-text v-if="memberId" class="user-profile__id">Member ID: {{ memberId }}</ion-text>
      <div class="user-profile__holder-rating">
        <star-rating class="user-profile__rating " :model-value="userRating" readonly/>
        <ion-text
          class="user-profile__feedback"
          v-if="showFeedback"
        >
          (from {{ feedbacksCount }} review(s))
        </ion-text>
      </div>
      <ion-button
        class="user-profile__button"
        fill="clear"
        v-if="!hideFeedbackButton"
        @click="isUser ? navigate({name: RouteNamesEnum.UserFeedbacks, id: userId}) : isDesktop ? navigate({name: RouteNamesEnum.DirectoryTeam, id: memberId, query: { tab: 3}}) : navigate({name: RouteNamesEnum.DirectoryDetails, id: memberId, query: { activeTab: 3 }})"
      >
        (<span>view feedback</span>)
      </ion-button>
    </div>
    <ion-button
      v-if="showEditButton"
      class="user-profile__button-edit"
      @click="navigateToUserEdit"
      fill="clear"
    >
      <ion-icon
        src="assets/icon/edit.svg"
      />
    </ion-button>
  </div>
</template>

<script setup lang="ts">
import { IonImg, IonIcon, IonButton, IonText } from '@ionic/vue';
import StarRating from '@/modules/General/components/StarRating.vue';
import { useBreakpoint } from "@/hooks/useBreakpoint";
import VueAvatar from "@webzlodimir/vue-avatar";
import "@webzlodimir/vue-avatar/dist/style.css";
import { RouteNamesEnum } from '@/const/routes';
import router from '@/router';
import { computed, ref } from 'vue';
import { capitalizeEachWord } from '@/helpers/formaters';
import { EventNamesEnum } from '@/const/events';

const props = withDefaults(
  defineProps<{
    hideFeedbackButton?: boolean;
    hideNoticeButton?: boolean;
    avatarSrc?: string;
    teamName?: string;
    userName?: string;
    titleId?: string;
    memberId?: string;
    userId?: string;
    userRating?: number;
    showFeedback?: boolean;
    isPrimary?: boolean;
    isTracking?: boolean;
    feedbacksCount?: number;
    showEditButton?: boolean;
    isUser?: boolean;
    hasUnreadEvents?: boolean;
  }>(),
  {
    avatarSrc: '',
    teamName: '',
    userName: '',
    titleId: 'Member ID',
    memberId: '',
    userId: '',
    userRating: 0,
    showFeedback: false,
    isPrimary: false,
    isTracking: false,
    showEditButton: false,
  }
);

const hasUnreadEvents = computed(() => props?.hasUnreadEvents)

const emits = defineEmits<{
  (e: EventNamesEnum.OnNavigate, item: any): void;
}>();

const navigate = (item: any) => {
  emits(EventNamesEnum.OnNavigate, item);
}

const imgExists = ref<boolean>(true);

const navigateToUserEdit = () => {
  router.push({
    name: RouteNamesEnum.UsersEdit,
    params: { id : props.userId }
  });
}

const { isDesktop } = useBreakpoint();
</script>

<style lang="scss" scoped>
.user-profile {
  text-align: center;

  &__button-edit {
    position: absolute;
    right: 0;
    top: -2px;
    font-size: 18px;
    line-height: 1;
    height: auto;
    --padding-end: 0;
    --padding-start: 0;
    --padding-top: 0;
    --padding-bottom: 0;
    --border-width: 0;
    --min-height: 0;
  }

  &__content {
    display: flex;
    text-align: left;
    position: relative;
    padding-bottom: 5px;
    align-items: center;
  }

  &__image {
    position: relative;
    width: 80px;
    height: 80px;
    padding: 4px;
    border-radius: 50%;
    border: 1px solid var(--ion-color-primary);

    &--is-primary {
      border: 1px solid var(--ion-color-success);
    }
  }

  &__avatar {
    width: 100%;
    height: 100%;
    object-fit: cover;
    
    &::part(image) {
      border-radius: 50%;
    }
  }

  &__icon-location {
    position: absolute;
    right: 0;
    bottom: 5px;
    width: 20px;
    height: 20px;
    font-size: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: translateX(0.5px);
    color: var(--ion-color-success);
    background: var(--ion-color-white);
    border-radius: 50%;

    ion-icon {
      height: 15px;
      width: 15px;
      display: block;
    }
  }

  &__info {
    flex: 1 1 auto;
    padding-left: 10px;
  }

  &__btn-notice {
    position: absolute;
    right: 0px;
    top: -40px;
    height: 40px;
    width: 40px;
    --color: var(--ion-color-dark-grey);
    &__badge {
      position: absolute;
      right: 3px;
      top: -9px;
      width: 8px;
      height: 8px;
      background: var(--ion-color-cancelled);
      border-radius: 50%;
    }
    ion-icon {
      width: 35px;
      height: 35px;
      margin: -7px 0 0 10px;
    }
    .icon-inner {
      width: 30px;
      height: 35px;
    }
  }

  &__team-name {
    display: block;
    font-family: var(--ion-font-family);
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.3px;
    text-transform: capitalize;
    color: var(--ion-color-dark);
  }

  &__name {
    display: block;
    font-family: var(--ion-font-family);
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 0.3px;
    text-transform: capitalize;
    color: var(--ion-color-dark);
  }

  &__id {
    display: block;
    margin-bottom: 4px;
    font-weight: 500;
    font-size: 10px;
    line-height: 15px;
    color: var(--ion-color-dark-grey);
    letter-spacing: 0.3px;
  }

  &__holder-rating {
    display: flex;
    align-items: center;
  }

  &__rating {
    margin-bottom: 4px;
  }

  &__button {
    display: inline-block;
    vertical-align: top;
    height: auto;
    font-weight: 300;
    font-size: 11px;
    line-height: 15px;
    text-transform: none;
    --padding-start: 0;
    --padding-end: 0;
    --padding-top: 0;
    --padding-bottom: 0;
    --color: var(--ion-color-dark-grey);
    span {
      display: inline-block;
      vertical-align: top;
      border-bottom: 1px solid var(--ion-color-dark-grey);
    }
  }

  &__feedback {
    margin-left: 7px;
    font-weight: 300;
    font-size: 10px;
    line-height: 1;
  }
}
</style>